import { useEffect, useState } from 'react'
import { api, type SourceDocument } from '../api'

/**
 * The document registry — TECHNICAL_DESIGN.md §5.5.
 *
 * `POST /api/documents` has been an author capability since §5.5 was built and
 * nothing in the application called it, so the only way to add a mandate was to
 * construct the request by hand. Clause review then asked for the document's id
 * in a browser prompt — an identifier no screen could give you. The queue was
 * unusable end to end, and the gap was invisible from a checkout because every
 * test supplied the id directly.
 *
 * **This registers a reference, not a file.** The DMS holds the document; this
 * holds its title, where it lives, and the hash we last saw — enough for a
 * citation to be followed and for §9.2's nightly check to notice the content
 * moving underneath rules that cite it.
 */
const KINDS = [
  { id: 'ima', label: 'Investment management agreement', blurb: 'The mandate itself' },
  { id: 'side_letter', label: 'Side letter', blurb: 'A later variation agreed with one client' },
  { id: 'prospectus', label: 'Prospectus', blurb: 'The offering document a fund is sold on' },
  { id: 'instruction', label: 'Client instruction', blurb: 'A one-off direction, usually by email' },
  { id: 'pack', label: 'Guideline pack', blurb: 'A spreadsheet or summary the client maintains' },
]

const BLANK = {
  title: '',
  kind: 'ima',
  dms_uri: '',
  version_label: '',
  effective_from: '',
  content_hash: '',
}

export function Documents({ canRegister }: { canRegister: boolean }) {
  const [docs, setDocs] = useState<SourceDocument[]>([])
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState(BLANK)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setError(null)
    try {
      setDocs(await api.documents())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  if (!loaded) return <p className="loading">Loading…</p>

  return (
    <section>
      <header className="page-head">
        <h2>Mandate documents</h2>
        <p className="note">
          The documents your limits come from — the agreement, the side letters,
          the client's own guideline pack. Every limit has to name one of these
          before it can be signed off, which is what makes{' '}
          <em>where does this rule come from</em> a question with an answer.
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="ok">{notice}</p>}

      {canRegister && (
        <div className="card">
          <h3>Add a document</h3>
          <form
            className="row-form"
            onSubmit={(e) => {
              e.preventDefault()
              setBusy(true)
              setError(null)
              setNotice(null)
              api
                .registerDocument({
                  title: form.title.trim(),
                  kind: form.kind,
                  dms_uri: form.dms_uri.trim(),
                  version_label: form.version_label.trim() || null,
                  effective_from: form.effective_from || null,
                  content_hash: form.content_hash.trim() || null,
                })
                .then(() => {
                  setNotice(
                    `${form.title.trim()} added. It is now offered on Clause review, where limits are matched to what it says.`,
                  )
                  setForm(BLANK)
                  return load()
                })
                .catch((e) => setError(e instanceof Error ? e.message : String(e)))
                .finally(() => setBusy(false))
            }}
          >
            <label>
              Title
              <input
                value={form.title}
                required
                title="What people call it — “Fairhaven Global Bond IMA”."
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              Kind
              <select
                value={form.kind}
                title="What sort of document it is. A side letter usually beats the agreement it varies."
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                {KINDS.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Where it lives
              <input
                value={form.dms_uri}
                required
                title="A link somebody inside your network can open — the document library, a file share. The file itself stays where it is."
                onChange={(e) => setForm({ ...form, dms_uri: e.target.value })}
              />
            </label>
            <label>
              Version
              <input
                value={form.version_label}
                title="Optional. “Executed 2024-03-11”, “v4”."
                onChange={(e) => setForm({ ...form, version_label: e.target.value })}
              />
            </label>
            <label>
              In force from
              <input
                type="date"
                value={form.effective_from}
                title="Optional. The date this version started to apply."
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              />
            </label>
            <button type="submit" disabled={busy}>
              Add
            </button>
          </form>
          <p className="note">
            {KINDS.find((k) => k.id === form.kind)?.blurb}. The file stays in your
            document system — this records where it is, so a citation can be
            followed back to it.
          </p>
        </div>
      )}

      <h3>{docs.length} document(s)</h3>
      {docs.length === 0 ? (
        <p className="empty">
          None yet. Limits cannot be signed off until they cite one.
        </p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Title</th>
              <th>Kind</th>
              <th>Version</th>
              <th>State</th>
              <th>Limits citing it</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} data-testid="document">
                <td>
                  {/* A `uploads://` document is one this appliance is actually
                      holding — a guideline pack the import stored, or a mailbox
                      attachment — so its title links to the bytes. Everything
                      else is a reference into the customer's own document
                      system, and linking the raw URI is the honest thing to do
                      with a string only their network can resolve. */}
                  {d.dms_uri.startsWith('uploads://') ? (
                    <a href={`/api/documents/${d.id}/content`} title="Held here — opens the file">
                      {d.title}
                    </a>
                  ) : (
                    <a href={d.dms_uri} title={d.dms_uri}>
                      {d.title}
                    </a>
                  )}
                </td>
                <td className="clause">
                  {KINDS.find((k) => k.id === d.kind)?.label ?? d.kind}
                </td>
                <td className="clause">{d.version_label ?? '—'}</td>
                <td className={d.status === 'unresolved' ? 'warn' : 'clause'}>
                  {d.status === 'current'
                    ? 'In force'
                    : d.status === 'superseded'
                      ? 'Replaced'
                      : 'Link broken'}
                </td>
                <td className="limit">{d.rules_citing}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="note">
        <strong>Link broken</strong> means the nightly check could not open the
        document where it used to be, or found different content there. Limits
        citing it still stand — but somebody should say whether they still should.
      </p>
    </section>
  )
}
